import { useCallback, useEffect, useState } from 'react'
import {
  loadExpenses,
  monthTotal,
  thisMonth,
  type Expense,
  type SharedSpace,
} from '@core/expenses'
import { formatCents } from '@core/money'
import { Card, Section } from '@design/components'
import { DocumentViewer } from '@features/documents/DocumentViewer'
import { ExpenseSheet } from './ExpenseSheet'

/**
 * What was spent in a shared space this month.
 *
 * One number first, because that is the question — "hoeveel geeft ze daar
 * uit" — and a list of receipts underneath for whoever wants to look. The
 * order matters: a list with a total buried at the bottom answers the question
 * only after you have read everything you did not ask about.
 *
 * The same component serves both roles. There is nothing here a family member
 * may not see, because the space being shared is exactly what that means.
 */
export function SpendingCard({
  space,
  refreshKey,
  canEdit = false,
  userId = '',
}: {
  space: SharedSpace
  refreshKey?: number
  /** Only needed when canEdit: a receipt added later is uploaded as this user. */
  userId?: string
  /** Admin: a row opens the expense to be corrected. Member: it opens the receipt. */
  canEdit?: boolean
}) {
  const [month, setMonth] = useState(thisMonth())
  const [total, setTotal] = useState<number | null>(null)
  const [expenses, setExpenses] = useState<Expense[]>([])
  const [open, setOpen] = useState(false)
  const [viewing, setViewing] = useState<string | null>(null)
  const [editing, setEditing] = useState<Expense | null>(null)

  const refresh = useCallback(() => {
    monthTotal(space.id, month).then(setTotal).catch(() => setTotal(0))
    loadExpenses(space.id, month).then(setExpenses).catch(() => setExpenses([]))
  }, [space.id, month])

  useEffect(refresh, [refresh, refreshKey])

  if (total === null) return null

  return (
    <Section title={space.name}>
      <Card>
        <div className="flex items-baseline justify-between gap-2">
          <button
            onClick={() => setMonth(shiftMonth(month, -1))}
            className="text-muted px-2 py-1"
            aria-label="Vorige maand"
          >
            ‹
          </button>
          <div className="text-center">
            <p className="text-3xl font-semibold tabular-nums">{formatCents(total)}</p>
            <p className="text-sm text-muted capitalize">{monthLabel(month)}</p>
          </div>
          <button
            onClick={() => setMonth(shiftMonth(month, 1))}
            className="text-muted px-2 py-1"
            aria-label="Volgende maand"
            disabled={month >= thisMonth()}
          >
            ›
          </button>
        </div>

        {expenses.length > 0 && (
          <>
            <button
              onClick={() => setOpen((o) => !o)}
              className="w-full mt-2 py-2 text-sm text-muted"
            >
              {open ? 'Verbergen' : `Waaraan? (${expenses.length})`}
            </button>

            {open && (
              <div className="space-y-1 border-t border-line pt-2">
                {expenses.map((expense) => (
                  <button
                    key={expense.obligationId}
                    onClick={() =>
                      canEdit
                        ? setEditing(expense)
                        : expense.documentId && setViewing(expense.documentId)
                    }
                    className="w-full flex items-baseline justify-between gap-2 py-1 text-left"
                  >
                    <span className="min-w-0 truncate text-sm">
                      {expense.description}
                      {expense.documentId && <span className="text-muted"> · bon</span>}
                      {!expense.documentId && expense.awaitingReceipt && (
                        <span className="text-muted"> · bon volgt</span>
                      )}
                    </span>
                    <span className="shrink-0 tabular-nums text-sm">
                      {formatCents(expense.amountCents)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </Card>

      {viewing && <DocumentViewer documentId={viewing} onClose={() => setViewing(null)} />}

      {editing && (
        <ExpenseSheet
          expense={editing}
          spaceKind={space.kind}
          spaceId={space.id}
          userId={userId}
          onClose={() => setEditing(null)}
          onChanged={refresh}
        />
      )}
    </Section>
  )
}

function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number) as [number, number]
  const d = new Date(y, m - 1 + delta, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number]
  return new Intl.DateTimeFormat('nl-BE', { month: 'long', year: 'numeric' }).format(
    new Date(y, m - 1, 1),
  )
}
